import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  ArrowLeft,
  Check,
  CheckCircle2,
  ChevronRight,
  Database,
  FileSpreadsheet,
  Loader2,
  RefreshCw,
  Search,
  ShieldCheck,
  Trash2,
  Upload,
  X
} from 'lucide-react';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import api from '../services/api';
import IconButton from './IconButton';
import SearchableSelect from './SearchableSelect';

const IMPORT_FIELDS = [
  { key: 'nome_mov', label: 'Nome', required: true },
  { key: 'cpf_mov', label: 'CPF', required: false },
  { key: 'dt_nasc_mov', label: 'Data Nascimento', required: false },
  { key: 'matricula_mov', label: 'Matrícula', required: false },
  { key: 'id_grupo_mov', label: 'Grupo', required: false },
  { key: 'obs_mov', label: 'Observação', required: false }
];

// Colunas da prévia: o Sub não vem da planilha, é vinculado automaticamente pela empresa.
const PREVIEW_COLUMNS = [
  { key: 'nome_mov', label: 'Nome' },
  { key: 'cpf_mov', label: 'CPF' },
  { key: 'dt_nasc_mov', label: 'Data Nascimento' },
  { key: 'matricula_mov', label: 'Matrícula' },
  { key: 'id_grupo_mov', label: 'Grupo' },
  { key: 'id_sub_mov', label: 'Sub', derived: 'sub' },
  { key: 'obs_mov', label: 'Observação' }
];

const RESULT_TABS = [
  { key: 'TODOS', label: 'Todos' },
  { key: 'EXISTENTE', label: 'Manter' },
  { key: 'NOVO', label: 'Novos' },
  { key: 'AUSENTE_NO_EXCEL', label: 'Desativar' },
  { key: 'ERRO', label: 'Erros' }
];

const STEP_LABELS = [
  'Arquivo e Mapeamento',
  'Validando',
  'Resultados',
  'Confirmar',
  'Concluído'
];

const DEFAULT_GRUPO_ID = 1;

const normalizeText = (value, upper = false) => {
  if (value === null || value === undefined) return '';
  const text = String(value).trim();
  return upper ? text.toUpperCase() : text;
};

const parseExcelDate = (value) => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'number') {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (parsed?.y && parsed?.m && parsed?.d) {
      return `${parsed.y}-${String(parsed.m).padStart(2, '0')}-${String(parsed.d).padStart(2, '0')}`;
    }
    return '';
  }
  const text = String(value).trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(text)) {
    const [dd, mm, yyyy] = text.split('/');
    return `${yyyy}-${mm}-${dd}`;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  return '';
};

const formatPreviewValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
};

const summarizeRow = (row, { fallbackGrupoId = null, fallbackSubId = null } = {}) => {
  if (!row) return '-';
  const pieces = [];
  if (row.nome_mov) pieces.push(row.nome_mov);
  if (row.cpf_mov) pieces.push(`CPF ${row.cpf_mov}`);
  if (row.matricula_mov) pieces.push(`Matrícula ${row.matricula_mov}`);
  if (row.id_grupo_mov || fallbackGrupoId) pieces.push(`Grupo ${row.id_grupo_mov || fallbackGrupoId}`);
  if (row.id_sub_mov || fallbackSubId) pieces.push(`Sub ${row.id_sub_mov || fallbackSubId}`);
  return pieces.length ? pieces.join(' | ') : '-';
};

const badgeClasses = {
  EXISTENTE: 'bg-slate-100 text-slate-700',
  NOVO: 'bg-green-100 text-green-700',
  ATUALIZACAO_NECESSARIA: 'bg-amber-100 text-amber-700',
  AUSENTE_NO_EXCEL: 'bg-red-100 text-red-700',
  ERRO: 'bg-red-100 text-red-700'
};

const resultLabel = {
  EXISTENTE: 'Manter',
  NOVO: 'Novo',
  ATUALIZACAO_NECESSARIA: 'Atualizar',
  AUSENTE_NO_EXCEL: 'Desativar',
  ERRO: 'Erro'
};

const MovimentacaoValidationModal = ({
  isOpen,
  onClose,
  clientes = [],
  faixas = [],
  onCompleted
}) => {
  const fileInputRef = useRef(null);
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [sheetNames, setSheetNames] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rawData, setRawData] = useState([]);
  const [mapping, setMapping] = useState({});
  const [draggedToken, setDraggedToken] = useState(null);
  const [empresaId, setEmpresaId] = useState('');
  const [faixaId, setFaixaId] = useState('');
  const [comparisonKey, setComparisonKey] = useState('cpf_mov');
  const [activeTab, setActiveTab] = useState('TODOS');
  const [searchTerm, setSearchTerm] = useState('');
  const [validationPreview, setValidationPreview] = useState(null);
  const [validationRows, setValidationRows] = useState([]);
  const [validationToken, setValidationToken] = useState('');
  const [executedSummary, setExecutedSummary] = useState(null);
  const [fullSyncConfirmed, setFullSyncConfirmed] = useState(false);
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setFile(null);
      setSheetNames([]);
      setSelectedSheet('');
      setHeaders([]);
      setRawData([]);
      setMapping({});
      setDraggedToken(null);
      setEmpresaId('');
      setFaixaId('');
      setComparisonKey('cpf_mov');
      setActiveTab('TODOS');
      setSearchTerm('');
      setValidationPreview(null);
      setValidationRows([]);
      setValidationToken('');
      setExecutedSummary(null);
      setFullSyncConfirmed(false);
      setIsProcessingFile(false);
      setIsValidating(false);
      setIsConfirming(false);
    }
  }, [isOpen]);

  const selectedEmpresa = useMemo(
    () => clientes.find((cliente) => String(cliente.id_cli) === String(empresaId)) || null,
    [clientes, empresaId]
  );

  const empresaSub = selectedEmpresa?.cod_sub_cli ?? null;

  const duplicateHeaders = useMemo(() => {
    const counts = new Map();
    headers.forEach((header) => {
      const normalized = normalizeText(header, true);
      if (!normalized) return;
      counts.set(normalized, (counts.get(normalized) || 0) + 1);
    });
    return [...counts.entries()].filter(([, count]) => count > 1).map(([header]) => header);
  }, [headers]);

  const mappedData = useMemo(() => {
    return rawData
      .map((row) => {
        const mappedRow = {};
        IMPORT_FIELDS.forEach((field) => {
          const header = mapping[field.key];
          if (!header) return;
          const index = headers.indexOf(header);
          if (index === -1) return;
          let value = row[index];
          if (field.key === 'dt_nasc_mov') {
            value = parseExcelDate(value);
          } else if (field.key === 'nome_mov') {
            value = normalizeText(value, true);
          } else if (field.key === 'cpf_mov') {
            value = String(value || '').replace(/\D/g, '');
          } else {
            value = normalizeText(value);
          }
          mappedRow[field.key] = value;
        });
        return mappedRow;
      })
      .filter((row) => Object.values(row).some((value) => String(value || '').trim() !== ''));
  }, [headers, mapping, rawData]);

  const previewRows = useMemo(() => mappedData.slice(0, 5), [mappedData]);

  const filteredResults = useMemo(() => {
    const results = validationPreview?.results || [];
    const normalized = normalizeText(searchTerm, true);
    return results.filter((item) => {
      const tabOk = activeTab === 'TODOS' || item.result === activeTab;
      if (!tabOk) return false;
      if (!normalized) return true;

      const haystack = [
        item.keyValue,
        item.current?.nome_mov,
        item.incoming?.nome_mov,
        item.errorMessages?.join(' '),
        item.actionLabel
      ]
        .filter(Boolean)
        .join(' ')
        .toUpperCase();

      return haystack.includes(normalized);
    });
  }, [activeTab, searchTerm, validationPreview]);

  const tabCount = (tabKey) => {
    const results = validationPreview?.results || [];
    if (tabKey === 'TODOS') return results.length;
    return results.filter((item) => item.result === tabKey).length;
  };

  const autoMapFields = (fileHeaders) => {
    const nextMapping = {};
    IMPORT_FIELDS.forEach((field) => {
      const normalizedLabel = normalizeText(field.label, true);
      const normalizedKey = normalizeText(field.key, true);
      const directMatch = fileHeaders.find((header) => {
        const normalizedHeader = normalizeText(header, true);
        return normalizedHeader === normalizedLabel || normalizedHeader === normalizedKey;
      });

      if (directMatch) {
        nextMapping[field.key] = directMatch;
        return;
      }

      const keywordMatch = fileHeaders.find((header) => normalizeText(header, true).includes(normalizedLabel));
      if (keywordMatch) {
        nextMapping[field.key] = keywordMatch;
      }
    });
    setMapping(nextMapping);
  };

  const parseSheet = (workbook, sheetName, selectedFile) => {
    const worksheet = workbook.Sheets[sheetName];
    const sheetRows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
    if (!sheetRows.length) {
      toast.error('A planilha selecionada está vazia');
      setRawData([]);
      setHeaders([]);
      return;
    }

    const nextHeaders = Array.from(sheetRows[0]).map((header) => normalizeText(header));
    const nextRawData = sheetRows.slice(1);
    setFile(selectedFile);
    setHeaders(nextHeaders);
    setRawData(nextRawData);
    autoMapFields(nextHeaders);
  };

  const processFile = (selectedFile) => {
    if (!selectedFile) return;
    const isExcel = selectedFile.name.endsWith('.xlsx') || selectedFile.name.endsWith('.xls');
    if (!isExcel) {
      toast.error('Formato inválido. Envie um arquivo .xlsx ou .xls');
      return;
    }

    setIsProcessingFile(true);
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const workbook = XLSX.read(event.target.result, { type: 'array' });
        const names = workbook.SheetNames || [];
        if (!names.length) {
          toast.error('Nenhuma aba encontrada no arquivo');
          return;
        }

        setSheetNames(names);
        const firstSheet = names[0];
        setSelectedSheet(firstSheet);
        parseSheet(workbook, firstSheet, selectedFile);
      } catch (error) {
        console.error('Erro ao processar Excel:', error);
        toast.error('Não foi possível ler o arquivo Excel');
      } finally {
        setIsProcessingFile(false);
      }
    };

    reader.readAsArrayBuffer(selectedFile);
  };

  const handleFileChange = (event) => {
    processFile(event.target.files?.[0]);
  };

  const handleSheetChange = async (event) => {
    const newSheet = event.target.value;
    if (!file || !newSheet) return;

    setSelectedSheet(newSheet);
    setIsProcessingFile(true);
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      parseSheet(workbook, newSheet, file);
    } catch (error) {
      console.error('Erro ao trocar aba:', error);
      toast.error('Não foi possível carregar a aba selecionada');
    } finally {
      setIsProcessingFile(false);
    }
  };

  const handleDragStart = (event, header) => {
    setDraggedToken(header);
    event.dataTransfer.setData('text/plain', header);
  };

  const handleDrop = (event, fieldKey) => {
    event.preventDefault();
    const header = event.dataTransfer.getData('text/plain') || draggedToken;
    if (!header) return;
    setMapping((current) => ({ ...current, [fieldKey]: header }));
  };

  const requiredMissing = useMemo(
    () => IMPORT_FIELDS.filter((field) => field.required && !mapping[field.key]),
    [mapping]
  );

  const canValidate = Boolean(
    empresaId &&
      faixaId &&
      comparisonKey &&
      mapping[comparisonKey] &&
      file &&
      mappedData.length > 0 &&
      duplicateHeaders.length === 0 &&
      requiredMissing.length === 0
  );

  const validateImport = async () => {
    if (!canValidate) {
      toast.error('Preencha Empresa, Faixa, chave de comparação e mapeie os campos obrigatórios');
      return;
    }

    setIsValidating(true);
    setStep(2);
    try {
      const payload = {
        empresaId,
        faixaId,
        comparisonKey,
        fileName: file?.name || '',
        sheetName: selectedSheet,
        rows: mappedData
      };

      const response = await api.post('/movimentacao/validate-import', payload);
      setValidationPreview(response.data);
      setValidationRows(mappedData);
      setValidationToken(response.data.validationToken);
      setActiveTab('TODOS');
      setSearchTerm('');
      setStep(3);
    } catch (error) {
      const message = error?.response?.data?.message || 'Erro ao validar a importação';
      toast.error(message);
      setStep(1);
    } finally {
      setIsValidating(false);
    }
  };

  const confirmImport = async () => {
    if (!validationPreview?.canConfirm) {
      toast.error('Existem erros impeditivos. Corrija a planilha e valide novamente.');
      return;
    }
    if (!fullSyncConfirmed) {
      toast.error('Confirme que a planilha representa a lista completa deste escopo');
      return;
    }

    setIsConfirming(true);
    try {
      const payload = {
        empresaId,
        faixaId,
        comparisonKey,
        fileName: file?.name || '',
        sheetName: selectedSheet,
        rows: validationRows,
        validationToken,
        fullSyncConfirmed: true
      };

      const response = await api.post('/movimentacao/confirm-import', payload);
      setExecutedSummary(response.data);
      setStep(5);
      onCompleted?.();
    } catch (error) {
      const message = error?.response?.data?.message || 'Erro ao confirmar importação';
      toast.error(message);
      if (error?.response?.data?.preview) {
        setValidationPreview(error.response.data.preview);
        setValidationToken(error.response.data.preview.validationToken);
        setStep(3);
      }
    } finally {
      setIsConfirming(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-gray-600/75 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-6 w-6 text-blue-600" />
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Validar Importação</h3>
              <p className="text-sm text-gray-500">Assistente de sincronização por Empresa e Faixa</p>
            </div>
          </div>
          <IconButton icon={X} onClick={onClose} variant="secondary" title="Fechar" />
        </div>

        <div className="px-6 py-4 border-b bg-gray-50">
          <div className="grid grid-cols-5 gap-3">
            {STEP_LABELS.map((label, index) => {
              const current = index + 1;
              const completed = step > current;
              const active = step === current;
              return (
                <div key={label} className="flex items-center gap-3">
                  <div
                    className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                      completed
                        ? 'bg-green-600 text-white'
                        : active
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {completed ? <Check className="h-4 w-4" /> : current}
                  </div>
                  <span className={`text-sm ${active ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>{label}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {step === 1 && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Empresa *</label>
                  <SearchableSelect
                    options={clientes.map((cliente) => ({
                      value: cliente.id_cli,
                      label: cliente.nome_cli,
                      searchText: `${cliente.cd_supera_cli || ''} ${cliente.cnpj_cli || ''}`,
                      keywords: [cliente.cd_supera_cli, cliente.cnpj_cli]
                    }))}
                    value={empresaId}
                    onChange={(event) => setEmpresaId(event.target.value)}
                    placeholder="Busque por nome, código ou CNPJ"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Sub vinculado:{' '}
                    <span className="font-medium text-gray-700">
                      {empresaSub !== null && empresaSub !== '' ? empresaSub : '-'}
                    </span>
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Faixa *</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={faixaId}
                    onChange={(event) => setFaixaId(event.target.value)}
                  >
                    <option value="">Selecione a faixa</option>
                    {faixas.map((faixa) => (
                      <option key={faixa.id} value={faixa.id}>
                        {faixa.desc_faixa}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Chave de comparação *</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={comparisonKey}
                    onChange={(event) => setComparisonKey(event.target.value)}
                  >
                    <option value="cpf_mov">CPF</option>
                    <option value="matricula_mov">Matrícula</option>
                  </select>
                </div>
              </div>

              <div
                className="border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors p-8 text-center cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  processFile(event.dataTransfer.files?.[0]);
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                />
                <FileSpreadsheet className="h-14 w-14 text-gray-400 mx-auto mb-3" />
                <p className="text-lg font-medium text-gray-900">Selecione ou arraste a planilha Excel</p>
                <p className="text-sm text-gray-500 mt-1">Suporta arquivos .xlsx e .xls</p>
                {file && <p className="text-sm text-blue-700 mt-3 font-medium">{file.name}</p>}
              </div>

              {sheetNames.length > 1 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Aba da planilha</label>
                  <select
                    className="w-full md:w-80 px-3 py-2 border border-gray-300 rounded-md"
                    value={selectedSheet}
                    onChange={handleSheetChange}
                  >
                    {sheetNames.map((sheet) => (
                      <option key={sheet} value={sheet}>
                        {sheet}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {duplicateHeaders.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4 text-sm text-red-700">
                  Cabeçalhos duplicados encontrados: {duplicateHeaders.join(', ')}.
                </div>
              )}

              {isProcessingFile ? (
                <div className="flex items-center justify-center py-16 text-gray-600 gap-3">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Processando planilha...
                </div>
              ) : headers.length > 0 && (
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                  <div className="xl:col-span-1 space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-gray-700">Colunas do arquivo</h4>
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                        {headers.length} encontradas
                      </span>
                    </div>
                    <div className="border rounded-lg bg-gray-50 p-4 min-h-[320px]">
                      <div className="flex flex-wrap gap-2">
                        {headers.map((header, index) => {
                          const mapped = Object.values(mapping).includes(header);
                          return (
                            <div
                              key={`${header}-${index}`}
                              draggable
                              onDragStart={(event) => handleDragStart(event, header)}
                              className={`px-3 py-2 rounded-md text-sm border cursor-grab ${
                                mapped
                                  ? 'bg-green-50 border-green-200 text-green-700'
                                  : 'bg-white border-gray-200 text-gray-700'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                {mapped && <Check className="h-3 w-3" />}
                                {header || <span className="italic text-gray-400">(vazio)</span>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="xl:col-span-2 space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-gray-700">Mapeamento dos campos</h4>
                      <div className="flex gap-2">
                        <IconButton icon={RefreshCw} onClick={() => autoMapFields(headers)} variant="primary" size="sm" title="Remapear automático" />
                        <IconButton icon={Trash2} onClick={() => setMapping({})} variant="danger" size="sm" title="Limpar mapeamento" />
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 space-y-0.5">
                      <p>Sub: vinculado automaticamente pela empresa selecionada.</p>
                      <p>Grupo padrão para novos registros: 1 - Escalonado - IPA 100%.</p>
                    </div>
                    <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
                      {IMPORT_FIELDS.map((field) => (
                        <div
                          key={field.key}
                          onDragOver={(event) => event.preventDefault()}
                          onDrop={(event) => handleDrop(event, field.key)}
                          className={`border-2 border-dashed rounded-lg p-3 flex items-center gap-4 ${
                            mapping[field.key]
                              ? 'border-green-300 bg-green-50'
                              : field.required || field.key === comparisonKey
                                ? 'border-orange-200 bg-orange-50'
                                : 'border-gray-200 bg-gray-50'
                          }`}
                        >
                          <div className="min-w-[180px]">
                            <div className="text-sm font-medium text-gray-800">
                              {field.label}
                              {(field.required || field.key === comparisonKey) && <span className="text-red-500"> *</span>}
                            </div>
                            {field.key === comparisonKey && (
                              <div className="text-xs text-blue-700 mt-1">Usado como chave única da comparação</div>
                            )}
                          </div>

                          <div className="flex-1">
                            {mapping[field.key] ? (
                              <div className="bg-white border border-green-200 rounded-md px-3 py-2 flex items-center justify-between">
                                <span className="text-sm text-gray-800 truncate">{mapping[field.key]}</span>
                                <IconButton
                                  icon={X}
                                  onClick={() => {
                                    setMapping((current) => {
                                      const next = { ...current };
                                      delete next[field.key];
                                      return next;
                                    });
                                  }}
                                  variant="secondary"
                                  size="xs"
                                  title={`Remover mapeamento de ${field.label}`}
                                />
                              </div>
                            ) : (
                              <div className="text-sm text-gray-400 italic text-center py-2">Arraste uma coluna aqui</div>
                            )}
                          </div>

                          <div className="w-52">
                            <select
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                              value={mapping[field.key] || ''}
                              onChange={(event) => {
                                const value = event.target.value;
                                setMapping((current) => {
                                  const next = { ...current };
                                  if (!value) delete next[field.key];
                                  else next[field.key] = value;
                                  return next;
                                });
                              }}
                            >
                              <option value="">Selecionar coluna...</option>
                              {headers.map((header) => (
                                <option key={header} value={header}>
                                  {header || '(vazio)'}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {previewRows.length > 0 && (
                <div className="border rounded-lg overflow-x-auto">
                  <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
                    <h4 className="font-medium text-gray-700">Prévia da planilha</h4>
                    <span className="text-xs text-gray-500">Primeiras 5 linhas</span>
                  </div>
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {PREVIEW_COLUMNS.map((column) => (
                          <th key={column.key} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            {column.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {previewRows.map((row, index) => (
                        <tr key={index}>
                          {PREVIEW_COLUMNS.map((column) => (
                            <td key={column.key} className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">
                              {column.derived === 'sub'
                                ? (empresaSub !== null && empresaSub !== '' ? empresaSub : '-')
                                : column.key === 'id_grupo_mov'
                                  ? formatPreviewValue(row[column.key] || DEFAULT_GRUPO_ID)
                                : formatPreviewValue(row[column.key])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="min-h-[420px] flex flex-col items-center justify-center text-center">
              <Loader2 className="h-12 w-12 text-blue-600 animate-spin mb-4" />
              <h4 className="text-xl font-semibold text-gray-900">Validando e comparando os registros</h4>
              <p className="text-gray-500 mt-2 max-w-xl">
                Estamos comparando a planilha com os registros existentes do escopo selecionado. Nenhuma alteração está sendo feita no banco nesta etapa.
              </p>
            </div>
          )}

          {step === 3 && validationPreview && (
            <div className="space-y-6">
              {validationPreview.blockingErrors?.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                    <div className="text-sm text-red-700">
                      {validationPreview.blockingErrors.map((errorMessage) => (
                        <div key={errorMessage}>{errorMessage}</div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 lg:grid-cols-7 gap-4">
                {[
                  { label: 'Excel', value: validationPreview.summary.excelTotal },
                  { label: 'Banco escopo', value: validationPreview.summary.bancoEscopoAtivos },
                  { label: 'Manter', value: validationPreview.summary.manter },
                  { label: 'Novos', value: validationPreview.summary.novos },
                  { label: 'Desativar', value: validationPreview.summary.desativar },
                  { label: 'Erros', value: validationPreview.summary.erros }
                ].map((card) => (
                  <div key={card.label} className="bg-white border rounded-lg p-4">
                    <div className="text-xs uppercase tracking-wide text-gray-500">{card.label}</div>
                    <div className="text-2xl font-bold text-gray-900 mt-1">{card.value}</div>
                  </div>
                ))}
              </div>

              <div className="bg-gray-50 border rounded-lg p-4 grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <div className="text-xs uppercase text-gray-500">Arquivo</div>
                  <div className="text-sm font-medium text-gray-900">{validationPreview.summary.arquivo || '-'}</div>
                </div>
                <div>
                  <div className="text-xs uppercase text-gray-500">Planilha</div>
                  <div className="text-sm font-medium text-gray-900">{validationPreview.summary.planilha || '-'}</div>
                </div>
                <div>
                  <div className="text-xs uppercase text-gray-500">Empresa</div>
                  <div className="text-sm font-medium text-gray-900">{validationPreview.scope.empresa.nome_cli}</div>
                </div>
                <div>
                  <div className="text-xs uppercase text-gray-500">Faixa</div>
                  <div className="text-sm font-medium text-gray-900">{validationPreview.scope.faixa.desc_faixa}</div>
                </div>
              </div>

              <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
                <div className="flex flex-wrap gap-2">
                  {RESULT_TABS.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => setActiveTab(tab.key)}
                      className={`px-3 py-2 rounded-full text-sm border ${
                        activeTab === tab.key
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-200'
                      }`}
                    >
                      {tab.label} ({tabCount(tab.key)})
                    </button>
                  ))}
                </div>

                <div className="relative lg:w-80">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm"
                    placeholder="Pesquisar nos resultados"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                  />
                </div>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Resultado</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Linha / ID</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Chave</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Atual</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Planilha</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Detalhes</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {filteredResults.map((item, index) => (
                        <tr key={`${item.result}-${item.keyValue || 'sem-chave'}-${item.rowNumber || index}`}>
                          <td className="px-3 py-3 align-top">
                            <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${badgeClasses[item.result] || 'bg-gray-100 text-gray-700'}`}>
                              {resultLabel[item.result] || item.result}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-sm text-gray-700 align-top">
                            {item.rowNumber ? `Linha ${item.rowNumber}` : `ID ${item.metadata?.id_mov || '-'}`}
                          </td>
                          <td className="px-3 py-3 text-sm text-gray-700 align-top">{item.keyValue || '-'}</td>
                          <td className="px-3 py-3 text-sm text-gray-700 align-top max-w-[300px]">
                            {summarizeRow(item.current)}
                          </td>
                          <td className="px-3 py-3 text-sm text-gray-700 align-top max-w-[300px]">
                            {summarizeRow(item.incoming, {
                              fallbackGrupoId: DEFAULT_GRUPO_ID,
                              fallbackSubId: empresaSub
                            })}
                          </td>
                          <td className="px-3 py-3 text-sm text-gray-700 align-top min-w-[280px]">
                            {item.errorMessages?.length > 0 && (
                              <div className="space-y-1">
                                {item.errorMessages.map((message) => (
                                  <div key={message} className="text-red-600">{message}</div>
                                ))}
                              </div>
                            )}
                            {item.diffs?.length > 0 && (
                              <div className="space-y-1">
                                {item.diffs.map((diff) => (
                                  <div key={`${item.keyValue}-${diff.field}`} className="text-xs bg-amber-50 border border-amber-200 rounded px-2 py-1">
                                    <span className="font-medium">{diff.label}:</span> {diff.current} <ChevronRight className="inline h-3 w-3" /> {diff.incoming}
                                  </div>
                                ))}
                              </div>
                            )}
                            {!item.errorMessages?.length && !item.diffs?.length && (
                              <span className="text-gray-500">{item.actionLabel}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {step === 4 && validationPreview && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Database className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <div className="font-medium">Resumo da sincronização</div>
                    <div className="mt-1">
                      Serão inseridos {validationPreview.summary.novos}, mantidos {validationPreview.summary.manter}
                      e desativados {validationPreview.summary.desativar} registros.
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border rounded-lg p-4">
                  <div className="text-xs uppercase text-gray-500">Empresa</div>
                  <div className="text-lg font-semibold text-gray-900">{validationPreview.scope.empresa.nome_cli}</div>
                  <div className="text-sm text-gray-500 mt-1">Código: {validationPreview.scope.empresa.cd_supera_cli || '-'}</div>
                </div>
                <div className="border rounded-lg p-4">
                  <div className="text-xs uppercase text-gray-500">Faixa</div>
                  <div className="text-lg font-semibold text-gray-900">{validationPreview.scope.faixa.desc_faixa}</div>
                  <div className="text-sm text-gray-500 mt-1">Chave: {validationPreview.scope.comparisonLabel}</div>
                </div>
              </div>

              <label className="flex items-start gap-3 border rounded-lg p-4 bg-amber-50 border-amber-200">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={fullSyncConfirmed}
                  onChange={(event) => setFullSyncConfirmed(event.target.checked)}
                />
                <div className="text-sm text-amber-900">
                  Confirmo que esta planilha representa a lista completa e oficial deste escopo.
                  Registros ativos ausentes na planilha serão desativados logicamente.
                </div>
              </label>
            </div>
          )}

          {step === 5 && executedSummary && (
            <div className="min-h-[420px] flex flex-col items-center justify-center text-center">
              <CheckCircle2 className="h-14 w-14 text-green-600 mb-4" />
              <h4 className="text-2xl font-semibold text-gray-900">Importação concluída</h4>
              <p className="text-gray-500 mt-2">
                A sincronização do escopo {executedSummary.scope.empresa.nome_cli} / {executedSummary.scope.faixa.desc_faixa} foi concluída com sucesso.
              </p>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-8 w-full max-w-4xl">
                {[
                  { label: 'Inseridos', value: executedSummary.summary.inserted },
                  { label: 'Atualizados', value: executedSummary.summary.updated },
                  { label: 'Reativados', value: executedSummary.summary.reactivated },
                  { label: 'Desativados', value: executedSummary.summary.deactivated },
                  { label: 'Ativos após', value: executedSummary.summary.activeAfter }
                ].map((card) => (
                  <div key={card.label} className="border rounded-lg p-4 bg-white">
                    <div className="text-xs uppercase text-gray-500">{card.label}</div>
                    <div className="text-2xl font-bold text-gray-900 mt-1">{card.value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between">
          <IconButton icon={X} onClick={onClose} variant="secondary" title={step === 5 ? 'Fechar' : 'Cancelar'} />

          <div className="flex items-center gap-3">
            {step === 3 && (
              <IconButton
                icon={ArrowLeft}
                onClick={() => setStep(1)}
                variant="secondary"
                title="Voltar"
              />
            )}

            {step === 1 && (
              <IconButton
                icon={Upload}
                onClick={validateImport}
                disabled={!canValidate || isValidating}
                variant="success"
                title={isValidating ? 'Validando...' : 'Validar'}
              />
            )}

            {step === 3 && (
              <IconButton
                icon={Check}
                onClick={() => setStep(4)}
                disabled={!validationPreview?.canConfirm}
                variant="primary"
                title="Avançar para confirmação"
              />
            )}

            {step === 4 && (
              <>
                <IconButton
                  icon={ArrowLeft}
                  onClick={() => setStep(3)}
                  variant="secondary"
                  title="Voltar aos resultados"
                />
                <IconButton
                  icon={ShieldCheck}
                  onClick={confirmImport}
                  disabled={!fullSyncConfirmed || isConfirming}
                  variant="success"
                  title={isConfirming ? 'Confirmando...' : 'Confirmar Importação'}
                />
              </>
            )}

            {step === 5 && (
              <IconButton
                icon={Check}
                onClick={onClose}
                variant="primary"
                title="Concluir"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MovimentacaoValidationModal;
